import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarPlus, Pencil, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAlterarVencimento } from "@/hooks/use-conferencia";
import { fmtDataBR } from "@/lib/formato-mercado";
import {
  ATALHOS_VENCIMENTO_DIAS,
  checarVencimento,
  mensagemDeFalhaDeData,
  vencimentoDoAtalho,
} from "@shared/mercado";

/**
 * O seletor de vencimento — UM componente, duas telas.
 *
 * ═══ POR QUE ELE EXISTE ══════════════════════════════════════════════════════
 *
 * O prazo morava na formalização, então 15 dos 16 romaneios em aberto estavam
 * sem data: sem formalizar não havia onde gravar. Com o vencimento na venda
 * (061), ele se define nos dois lugares onde a pergunta aparece — no card
 * ROMANEIO DE CARGA da venda e na própria linha de "A receber". A linha é o que
 * resolve quinze de uma sentada, sem abrir romaneio por romaneio.
 *
 * ⚠️ OS ATALHOS CONTAM DA DATA DA VENDA, nunca de hoje: o romaneio de 27/07 com
 * "15 dias" vence em 11/08 — vencido —, e é essa a verdade. Contar de hoje
 * transformaria cada toque num prazo novo, inventado na hora da digitação.
 *
 * ⚠️ Vencimento no passado é PERMITIDO (dívida atrasada é fato) e limpar também.
 * O que o servidor recusa é vencer antes de a carga sair — e a checagem aqui é a
 * MESMA função do servidor, não uma cópia: o produtor vê o erro antes de tocar.
 *
 * ⚠️ "escolher data" é `<input type=date>`, e é decisão mobile-first: no celular
 * o teclado nativo JÁ é o calendário, e é o mesmo campo de todas as outras datas
 * do app. Um date-picker próprio aqui traria dependência nova para repetir o que
 * o sistema faz melhor.
 */
export function SeletorVencimento({
  /** VENDA no lado do produtor; COMPARTILHAMENTO no do comprador — ver o hook. */
  id,
  lado = "produtor",
  vencimento,
  dataVenda,
  /** Já formalizado? Então o link do comprador mostra o dado vivo — avise antes. */
  formalizado = false,
  testIdSufixo,
}: {
  id: string;
  lado?: "produtor" | "comprador";
  vencimento: string | null;
  dataVenda: string;
  formalizado?: boolean;
  testIdSufixo: string;
}) {
  const { toast } = useToast();
  const alterar = useAlterarVencimento(lado);
  const [aberto, setAberto] = useState(false);
  const [escolhida, setEscolhida] = useState("");

  async function gravar(novo: string | null) {
    const falha = novo ? checarVencimento(novo, dataVenda) : null;
    if (falha) {
      toast({ title: "Data não serve", description: mensagemDeFalhaDeData(falha, "vencimento"), variant: "destructive" });
      return;
    }
    try {
      await alterar.mutateAsync({ id, vencimento: novo });
      setAberto(false);
      setEscolhida("");
      toast({
        title: novo ? `Vencimento: ${fmtDataBR(novo)}` : "Vencimento removido.",
        description: formalizado ? "O link do comprador passa a mostrar o novo vencimento." : undefined,
      });
    } catch (e: any) {
      // Nunca `catch {}`: prazo que "salvou" sem salvar é o tipo de silêncio que
      // faz o produtor cobrar na data errada.
      toast({ title: "Não deu para gravar o vencimento", description: e?.message, variant: "destructive" });
    }
  }

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        {vencimento ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-sm underline underline-offset-2 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid={`button-vencimento-${testIdSufixo}`}
          >
            {fmtDataBR(vencimento)}<Pencil className="h-3 w-3" />
          </button>
        ) : (
          <button
            type="button"
            className="inline-flex min-h-8 items-center gap-1 rounded-md px-1.5 text-muted-foreground underline underline-offset-2 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid={`button-vencimento-${testIdSufixo}`}
          >
            <CalendarPlus className="h-3.5 w-3.5" />a definir
          </button>
        )}
      </PopoverTrigger>

      <PopoverContent className="w-64 space-y-2" align="start">
        <div className="flex gap-1">
          {ATALHOS_VENCIMENTO_DIAS.map(dias => (
            <Button
              key={dias}
              variant="outline"
              size="sm"
              className="min-h-10 flex-1 px-1"
              disabled={alterar.isPending}
              onClick={() => gravar(vencimentoDoAtalho(dataVenda, dias))}
              data-testid={`button-vencimento-${dias}d-${testIdSufixo}`}
            >
              {dias} dias
            </Button>
          ))}
        </div>
        {/* O prazo conta da VENDA: o produtor precisa ver de onde. */}
        <p className="text-[11px] text-muted-foreground">A partir da venda, {fmtDataBR(dataVenda)}.</p>

        <div className="flex gap-1">
          <Input
            type="date"
            className="min-h-10"
            value={escolhida}
            min={dataVenda}
            onChange={e => setEscolhida(e.target.value)}
            data-testid={`input-vencimento-${testIdSufixo}`}
          />
          <Button
            size="sm"
            className="min-h-10 shrink-0"
            disabled={!escolhida || alterar.isPending}
            onClick={() => escolhida && gravar(escolhida)}
            data-testid={`button-vencimento-escolher-${testIdSufixo}`}
          >
            Ir
          </Button>
        </div>

        {vencimento && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground"
            disabled={alterar.isPending}
            onClick={() => gravar(null)}
            data-testid={`button-vencimento-limpar-${testIdSufixo}`}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />Sem vencimento
          </Button>
        )}

        {/* O link renderiza o dado VIVO: o produtor precisa saber disso ANTES de
            trocar, não depois que o comprador perguntar. */}
        {formalizado && (
          <p className="text-[11px] text-atencao" data-testid={`aviso-vencimento-formalizado-${testIdSufixo}`}>
            O link do comprador passa a mostrar o novo vencimento.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
